import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import ActionTextBtn from 'src/components/shared/form-inputs/ActionTextBtn';
import { setInitialtUserJobScheduleId } from 'src/functions/general';
import { getLastCrsResult } from 'src/my_pages/cybersecurity-risk-score/crsService';
import { getRiskLabel, getRiskPill, getRiskTone, isCrsScoreReadyStatus } from 'src/my_pages/cybersecurity-risk-score/crsScore';
import type { CyberRiskScoreResult } from 'src/my_pages/cybersecurity-risk-score/types';

type CrsCellState = 'loading' | 'score' | 'calculate';

const scoreFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

// CRS cell for a Targets table row:
// - scan not completed  -> locked (CRS can only run after a completed scan)
// - completed + score    -> colored score pill (click to view details)
// - completed + no score -> "Calculate" button (click to open the CRS questionnaire)
const CrsScoreCell = ({ row }: { row: any }) => {
  const navigate = useNavigate();

  const isCompleted = row?.status?.isCompleted === true;
  // Mirror the CRS page id derivation (active_domain_data.userDomainId || id).
  const domainId = row?.userDomainId ?? row?.id;

  const [state, setState] = useState<CrsCellState>('loading');
  const [result, setResult] = useState<CyberRiskScoreResult | null>(null);

  useEffect(() => {
    if (!isCompleted) return undefined;
    if (!domainId) {
      setState('calculate');
      return undefined;
    }

    let isMounted = true;
    setState('loading');

    getLastCrsResult(String(domainId))
      .then((res) => {
        if (!isMounted) return;
        if (res && isCrsScoreReadyStatus(res.status) && typeof res.finalScore === 'number') {
          setResult(res);
          setState('score');
        } else {
          setState('calculate');
        }
      })
      .catch(() => {
        // 400 CyberRiskScoreNotFound / missing profile / transient errors -> let the user calculate.
        if (isMounted) setState('calculate');
      });

    return () => {
      isMounted = false;
    };
  }, [isCompleted, domainId]);

  const goToCrs = async (event: React.MouseEvent) => {
    event.stopPropagation();
    const selected = await setInitialtUserJobScheduleId(row?.lastWebsiteVulnerabilityId);
    if (!selected) return;
    navigate('/cybersecurity-risk-score');
  };

  if (!isCompleted) {
    return (
      <Tooltip title="Cyber Risk Score can be run once the scan is completed." enterDelay={300}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
          <LockOutlinedIcon fontSize="small" />
        </Box>
      </Tooltip>
    );
  }

  if (state === 'loading') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={18} />
      </Box>
    );
  }

  if (state === 'score' && result) {
    const score = result.finalScore;
    const tone = getRiskTone(score);

    return (
      <Tooltip title={`${getRiskLabel(score)} • ${scoreFormatter.format(score)}/10 — view details`} enterDelay={300}>
        <Box
          onClick={goToCrs}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            cursor: 'pointer',
            height: 30,
            px: 1.5,
            borderRadius: '999px',
            border: '1px solid',
            borderColor: tone.border,
            bgcolor: tone.background,
            color: tone.main,
            transition: 'all 0.15s ease',
            '&:hover': { borderColor: tone.main },
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1 }}>{scoreFormatter.format(score)}</Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 600, lineHeight: 1 }}>{getRiskPill(score)}</Typography>
        </Box>
      </Tooltip>
    );
  }

  return (
    <ActionTextBtn
      label="Calculate"
      tooltipTitle="Calculate Cyber Risk Score"
      startIcon={<CalculateOutlinedIcon fontSize="small" />}
      onClick={goToCrs}
      sx={{
        height: 30,
        borderColor: 'primary.light',
        bgcolor: 'primary.light',
        color: 'primary.main',
        fontWeight: 600,
        '&:hover': { bgcolor: 'primary.main', borderColor: 'primary.main', color: 'common.white' },
      }}
    />
  );
};

export default CrsScoreCell;
